import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { type MouseEvent, useId, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { Dropdown, useDropdownContext } from "../Dropdown";
import { Typography } from "../Typography";
import { MenuContent } from "./MenuContent";
import { useMenuContext } from "./context";
import { isMenuOption, type IMenuOption } from "./types";

type MenuOptionItemProps = {
  option: IMenuOption;
  index: number;
};

export const MenuOptionItem = ({ option, index }: MenuOptionItemProps) => {
  const { onSelect, rtl, rootTriggerRef } = useMenuContext();
  const {
    navigationRef,
    getItemProps,
    activeIndex,
    dismiss,
    contentId: parentContentId,
  } = useDropdownContext();

  const [open, setOpen] = useState<boolean>(false);
  const isActive = activeIndex === index;

  const optionId = `${parentContentId}-option-${index}`;
  const contentId = useId();

  const submenuTypeaheadListRef = useRef<(string | null)[]>([]);
  useMemo(() => {
    if (option.options) {
      submenuTypeaheadListRef.current = option.options
        .filter(isMenuOption)
        .map((opt) => opt.text);
    }
  }, [option.options]);

  const handleSelect = (e: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (option.options) {
      return;
    }

    onSelect(option.id);
    dismiss();

    setTimeout(() => {
      rootTriggerRef?.current?.focus();
    }, 0);
  };

  const triggerElement = (
    <button
      {...(option.dataAttributes || {})}
      id={optionId}
      role="menuitem"
      aria-disabled={option.disabled}
      aria-haspopup={option.options ? "menu" : undefined}
      aria-expanded={option.options ? open : undefined}
      className={clsx(
        "nuco-menu__option",
        option.disabled && "nuco-menu__option--disabled",
        option.className,
      )}
      disabled={option.disabled}
      ref={navigationRef(index)}
      {...getItemProps?.()}
      onClick={handleSelect}
      tabIndex={isActive ? 0 : -1}
    >
      {option.icon && (
        <span className="nuco-menu__option-startAccessory">{option.icon}</span>
      )}
      <Typography variant="body" className="flex-1 text-start">
        {option.text}
      </Typography>
      {option.accessory && option.accessory}
      {option.options && (
        <span className="nuco-menu__option-endAccessory">
          {rtl ? (
            <ChevronLeftIcon size="0.875rem" />
          ) : (
            <ChevronRightIcon size="0.875rem" />
          )}
        </span>
      )}
    </button>
  );

  if (option.options) {
    return (
      <Dropdown
        trigger={triggerElement}
        content={<MenuContent options={option.options} />}
        triggerId={optionId}
        contentId={contentId}
        open={open}
        onOpenChange={setOpen}
        interactions={[
          "hover",
          "click",
          "dismiss",
          "listNavigation",
          "typeahead",
        ]}
        role="menu"
        placement={rtl ? "left-start" : "right-start"}
        offset={0}
        hoverOptions={{
          delay: { open: 100, close: 0 },
        }}
        listNavigationOptions={{
          nested: true,
          parentOrientation: "vertical",
          rtl,
        }}
        floatingFocusManager={{
          initialFocus: -1,
          returnFocus: true,
          restoreFocus: false,
          modal: false,
        }}
        ariaLabel={`${option.text} submenu`}
        typeaheadListRef={submenuTypeaheadListRef}
      />
    );
  }

  return triggerElement;
};
